import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ToastProvider } from "@/contexts/ToastContext";
import { RegistrationProvider } from "@/contexts/RegistrationContext";
import Layout from "@/components/layout/Layout";
import Home from "@/pages/common/Home";
import Login from "@/pages/auth/Login";
import AdminLogin from "@/pages/auth/AdminLogin";
import Register from "@/pages/auth/Register";
import VerifyOTP from "@/pages/auth/VerifyOTP";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import EmailVerificationRequired from "@/pages/auth/EmailVerificationRequired";
import AwaitingApproval from "@/pages/auth/AwaitingApproval";
import ContactUs from "@/pages/common/ContactUs";
import PrivacyPolicy from "@/pages/common/PrivacyPolicy";
import TermsOfService from "@/pages/common/TermsOfService";
import AIHackathonTermsOfService from "@/pages/common/AIHackathonTermsOfService";
import AboutUs from "@/pages/common/AboutUs";
import EventList from "@/pages/events/EventList";
import EventDetail from "@/pages/events/EventDetail";
import EventRegistration from "@/pages/events/EventRegistration";
import RegistrationSuccess from "@/pages/events/RegistrationSuccess";

import EventManagement from "@/pages/admin/EventManagement";
import TeamManagement from "@/pages/admin/TeamManagement";
import Jobs from "@/pages/jobs/Jobs";
import PostJob from "@/pages/jobs/PostJob";
import ViewJobs from "@/pages/jobs/ViewJobs";
import JobDetails from "@/pages/jobs/JobDetails";
import BugReport from "@/pages/support/BugReport";
import Support from "@/pages/support/Support";
import ViewTickets from "@/pages/support/ViewTickets";
import AdminDashboard from "@/pages/dashboard/AdminDashboard";
import MentorDashboard from "@/pages/dashboard/MentorDashboard";
import AspirantDashboard from "@/pages/dashboard/AspirantDashboard";
import AspirantProfile from "@/pages/profile/AspirantProfile";
import MentorProfile from "@/pages/profile/MentorProfile";
import AdminProfile from "@/pages/profile/AdminProfile";
import FindMentors from "@/pages/mentors/FindMentors";
import HackathonList from "@/pages/hackathons/HackathonList";
import HackathonDetail from "@/pages/hackathons/HackathonDetail";
import AdminHackathonDetail from "@/pages/admin/HackathonDetail";
import TeamCreate from "@/pages/teams/TeamCreate";
import TeamDetail from "@/pages/teams/TeamDetail";
import TeamJoin from "@/pages/teams/TeamJoin";
import TeamDiscovery from "@/pages/teams/TeamDiscovery";
import MyTeams from "@/pages/teams/MyTeams";
import MentorTeams from "@/pages/teams/MentorTeams";
import AddMembers from "@/pages/teams/AddMembers";
import HackathonManagement from "@/pages/admin/HackathonManagement";
import HackathonCreate from "@/pages/admin/HackathonCreate";
import HackathonEdit from "@/pages/admin/HackathonEdit";
import HighlightsManagement from "@/pages/admin/HighlightsManagement";
import CheckIn from "@/pages/admin/CheckIn";
import Highlights from "@/pages/Highlights";

import { useAuth } from "@/contexts/AuthContext";
import clarityService from "@/services/clarityService";
import AdminContentManager from "@/pages/dashboard/AdminContentManager";
import Unsubscribe from "@/pages/Unsubscribe";
import AdminAiPromptsPage from "@/pages/dashboard/AdminAiPromptsPage";
import { useFavicon } from "@/hooks/useFavicon";

// Page tracking component for Clarity
const PageTracker = () => {
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    // Track page view
    const pageName = location.pathname;
    clarityService.trackPageView(pageName, {
      userRole: user?.role || 'anonymous',
      isAuthenticated: !!user
    });

    // Identify user if authenticated
    if (user) {
      clarityService.identify(user._id, user.role as 'aspirant' | 'mentor' | 'admin');
    }
  }, [location.pathname, user]);

  return null;
};

// Simple authentication check component
const RequireAuth = ({
  children,
  requiredRole,
  loginPath = "/login",
}: {
  children: React.ReactNode;
  requiredRole?: string;
  loginPath?: string;
}) => {
  const { isAuthenticated, user, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to={loginPath} replace />;
  }

  if (user && user.role !== "admin") {
    if (!user.isEmailVerified) {
      return <Navigate to="/verify-otp" state={{ email: user.email }} replace />;
    }
    if (!user.isApproved) {
      return <Navigate to="/awaiting-approval" replace />;
    }
  }

  if (requiredRole && user?.role !== requiredRole) {
    // Redirect based on user role
    switch (user?.role) {
      case 'admin':
        return <Navigate to="/admin/dashboard" replace />;
      case 'mentor':
        return <Navigate to="/mentor/dashboard" replace />;
      case 'aspirant':
        return <Navigate to="/aspirant/dashboard" replace />;
      default:
        return <Navigate to="/" replace />;
    }
  }
  
  return <>{children}</>;
};

function App() {
  console.log('App: Component rendered');
  const { updateFavicon } = useFavicon();
  
  return (
    <AuthProvider>
      <ToastProvider>
        <RegistrationProvider>
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true
            }}
          >
            <Routes>
              {/* Routes with Layout */}
              <Route path="/*" element={
                <Layout>
                  <PageTracker />
                  <Routes>
                    {/* Public Routes - No authentication required */}
                    <Route path="/" element={<Home />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/admin/login" element={<AdminLogin />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/verify-otp" element={<VerifyOTP />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/contact-us" element={<ContactUs />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/terms-of-service" element={<TermsOfService />} />
                    <Route path="/ai-hackathon-terms" element={<AIHackathonTermsOfService />} />
                    <Route path="/about-us" element={<AboutUs />} />
                    <Route path="/verify-email" element={<EmailVerificationRequired />} />
                    <Route path="/awaiting-approval" element={<AwaitingApproval />} />
                    <Route path="/events" element={<EventList />} />

                    <Route path="/events/:id" element={<EventDetail />} />
                    <Route path="/events/:id/register" element={<EventRegistration />} />
                    <Route path="/registration/success/:registrationId" element={<RegistrationSuccess />} />
                    <Route path="/jobs" element={<Jobs />} />
                    <Route path="/mentors" element={<FindMentors />} />
                    <Route path="/highlights" element={<Highlights />} />
                    
                    {/* Protected Routes - Authentication required */}
                    <Route path="/admin/dashboard" element={
                      <RequireAuth requiredRole="admin" loginPath="/admin/login">
                        <AdminDashboard />
                      </RequireAuth>
                    } />
                    <Route path="/unsubscribe" element={<Unsubscribe />} />
                    <Route path="/admin/content" element={
                      <RequireAuth requiredRole="admin" loginPath="/admin/login">
                        <AdminContentManager />
                      </RequireAuth>
                    } />
                    <Route path="/admin/ai-prompts" element={
                      <RequireAuth requiredRole="admin" loginPath="/admin/login">
                        <AdminAiPromptsPage />
                      </RequireAuth>
                    } />
                    <Route path="/admin/events" element={
                      <RequireAuth requiredRole="admin" loginPath="/admin/login">
                        <EventManagement />
                      </RequireAuth>
                    } />
                    <Route path="/admin/teams" element={
                      <RequireAuth requiredRole="admin" loginPath="/admin/login">
                        <TeamManagement />
                      </RequireAuth>
                    } />
                    <Route path="/admin/hackathons" element={
                      <RequireAuth requiredRole="admin" loginPath="/admin/login">
                        <HackathonManagement />
                      </RequireAuth>
                    } />
                    <Route path="/admin/hackathons/create" element={
                      <RequireAuth requiredRole="admin" loginPath="/admin/login">
                        <HackathonCreate />
                      </RequireAuth>
                    } />
                    <Route path="/admin/hackathons/:id/edit" element={
                      <RequireAuth requiredRole="admin" loginPath="/admin/login">
                        <HackathonEdit />
                      </RequireAuth>
                    } />
                    <Route path="/admin/hackathons/:id/view" element={
                      <RequireAuth requiredRole="admin" loginPath="/admin/login">
                        <AdminHackathonDetail />
                      </RequireAuth>
                    } />
                    <Route path="/admin/check-in/:token" element={
                      <RequireAuth requiredRole="admin" loginPath="/admin/login">
                        <CheckIn />
                      </RequireAuth>
                    } />
                    <Route path="/admin/highlights" element={
                      <RequireAuth requiredRole="admin" loginPath="/admin/login">
                        <HighlightsManagement />
                      </RequireAuth>
                    } />
                    <Route path="/mentor/dashboard" element={
                      <RequireAuth requiredRole="mentor">
                        <MentorDashboard />
                      </RequireAuth>
                    } />
                    <Route path="/aspirant/dashboard" element={
                      <RequireAuth requiredRole="aspirant">
                        <AspirantDashboard />
                      </RequireAuth>
                    } />
                    
                    {/* Profile Routes */}
                    <Route path="/admin/profile" element={
                      <RequireAuth requiredRole="admin" loginPath="/admin/login">
                        <AdminProfile />
                      </RequireAuth>
                    } />
                    <Route path="/mentor/profile" element={
                      <RequireAuth requiredRole="mentor">
                        <MentorProfile />
                      </RequireAuth>
                    } />
                    <Route path="/aspirant/profile" element={
                      <RequireAuth requiredRole="aspirant">
                        <AspirantProfile />
                      </RequireAuth>
                    } />
                    
                    {/* Job Routes */}
                    <Route path="/jobs/post" element={
                      <RequireAuth>
                        <PostJob />
                      </RequireAuth>
                    } />
                    <Route path="/jobs/view" element={<ViewJobs />} />
                    <Route path="/jobs/:id" element={<JobDetails />} />
                    
                    {/* Team Routes */}
                    <Route path="/teams/create" element={
                      <RequireAuth>
                        <TeamCreate />
                      </RequireAuth>
                    } />
                    <Route path="/teams/join" element={
                      <RequireAuth>
                        <TeamJoin />
                      </RequireAuth>
                    } />
                    <Route path="/teams/my-teams" element={
                      <RequireAuth>
                        <MyTeams />
                      </RequireAuth>
                    } />
                    <Route path="/teams/:id" element={
                      <RequireAuth>
                        <TeamDetail />
                      </RequireAuth>
                    } />
                    <Route path="/teams/:teamId/add-members" element={
                      <RequireAuth>
                        <AddMembers />
                      </RequireAuth>
                    } />
                    <Route path="/hackathons/:hackathonId/teams/discover" element={
                      <RequireAuth>
                        <TeamDiscovery />
                      </RequireAuth>
                    } />
                    <Route path="/mentor/teams" element={
                      <RequireAuth requiredRole="mentor">
                        <MentorTeams />
                      </RequireAuth>
                    } />
                    
                    {/* Hackathon Routes */}
                    <Route path="/hackathons" element={<HackathonList />} />
                    <Route path="/hackathons/:id" element={<HackathonDetail />} />
                    
                    {/* Support Routes */}
                    <Route path="/support" element={<Support />} />
                    <Route path="/support/bug-report" element={
                      <RequireAuth>
                        <BugReport />
                      </RequireAuth>
                    } />
                    <Route path="/support/tickets" element={
                      <RequireAuth>
                        <ViewTickets />
                      </RequireAuth>
                    } />
                    
                    {/* Catch-all route */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Layout>
              } />
            </Routes>
          </Router>
        </RegistrationProvider>
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;
